import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import {
  useUpdateUserProfile,
  useUpdateUserProfileImg,
  useUserProfile,
} from 'app/core/api/user/api-user';
import { UserInfo } from 'app/core/api/user/api-user.types';
import EditableImage from 'app/shared/components/app-image/EditableImage';
import Button from 'app/shared/components/button/Button';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import {
  NAME_RULES,
  PHONE_RULES,
} from 'app/shared/components/form-input/utilities/form-tools';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import {
  DefaultUserImg,
  PadCollapse,
} from 'app/shared/components/misc';
import Form from 'app/shared/frames/Form';
import {
  useEffectOnChanges,
  useObservableBinding,
} from 'app/tools/hooks/misc-hooks';
import { useHistory } from 'react-router-dom';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { tap } from 'rxjs/operators';
import _ from 'lodash';

type FormValues = UserInfo;

export default function UserInfoEntry(): JSX.Element {
  const history = useHistory();

  const {
    resource: userInfo,
    loading,
    request: refresh,
  } = useUserProfile();
  const {
    request$: updateUserInfo$,
    sending: sendingUpdate,
    error: apiError,
  } = useUpdateUserProfile();
  const { request$: updateUserImg$, sending: sendingImg } =
    useUpdateUserProfileImg();
  const { showSnack } = useShowInfo();

  const { inputProps, handleSubmit, formState } =
    useInputForm<FormValues>({
      mode: 'onTouched',
      defaultValues: userInfo,
      apiError,
    });

  const [imageSet, imageSet$] = useObservableBinding<File | null>(
    null
  );

  const submit = (data: FormValues) => {
    if (_.isEmpty(formState.touched)) {
      history.push(`${ConsumerRoutePath.WALLET}`);
      return;
    }
    return updateUserInfo$(data).pipe(
      tap(() => {
        showSnack({
          message: 'Profile Updated!',
          alertType: 'success',
        });
        history.push(`${ConsumerRoutePath.PROFILE}`);
      })
    );
  };

  useEffectOnChanges(() => {
    updateUserImg$({ profilePhoto: imageSet }).subscribe(() =>
      refresh()
    );
    showSnack({
      message: 'Profile Picture Updated!',
      alertType: 'success',
    });
  }, [imageSet, refresh, updateUserImg$, showSnack]);

  const removeImage = () => {
    updateUserImg$({ profilePhoto: null }).subscribe(() => refresh());
    showSnack({
      message: 'Profile Picture Deleted!',
      alertType: 'error',
    });
  };

  return (
    <>
      <PadCollapse pad={2} />
      <EditableImage
        alt="user profile photo"
        currentSrc={userInfo?.profilePhoto}
        loadingImg={sendingImg || loading}
        usedCustomImg={!!userInfo?.profilePhoto}
        fallbackImgNode={<DefaultUserImg size={150} />}
        classes={{ img: 'w-15 h-15 rounded object-cover' }}
        {...{ imageSet$, removeImage }}
      />
      <PadCollapse pad={2} />
      <Form onSubmit={handleSubmit(submit)}>
        <FormTextInput
          controlName="firstName"
          rules={NAME_RULES}
          disabled={loading}
          {...inputProps}
        />
        <FormTextInput
          controlName="lastName"
          optional
          rules={NAME_RULES}
          disabled={loading}
          {...inputProps}
        />
        <FormTextInput
          controlName="mobile"
          optional
          rules={PHONE_RULES}
          disabled={loading}
          {...inputProps}
        />
        <Button
          type="submit"
          disabled={loading}
          pending={sendingUpdate}
        >
          Save
        </Button>
      </Form>
    </>
  );
}
